/**
 * LOCATION: dashboard-business/src/pages/Signup.js
 * Basic is free (no Paystack). Pro/Premium go through Paystack with NGN/USD toggle.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../services/auth';

const API_URL = 'https://janimony-backend.onrender.com/api';
const PS_KEY  = 'pk_live_77a192e2b64b00bf89a71f06be10fc9b27c4641b';

const PLANS = [
  {
    id: 'basic', label: 'Basic', icon: 'fa-seedling', color: '#6b7280',
    monthlyNGN: 0, yearlyNGN: 0, monthlyUSD: 0, yearlyUSD: 0,
    perks: ['50 messages/mo', 'AI chat', 'English only'],
  },
  {
    id: 'pro', label: 'Pro', icon: 'fa-bolt', color: '#667eea',
    monthlyNGN: 7500, yearlyNGN: 75000, monthlyUSD: 9, yearlyUSD: 90,
    perks: ['1,000 messages/mo', 'AI + Human agents', '6 languages'],
    popular: true,
  },
  {
    id: 'premium', label: 'Premium', icon: 'fa-crown', color: '#f59e0b',
    monthlyNGN: 18000, yearlyNGN: 180000, monthlyUSD: 20, yearlyUSD: 200,
    perks: ['Unlimited messages', 'All languages', 'Unlimited agents'],
  },
];

export default function Signup() {
  const navigate = useNavigate();
  const [form,     setForm]     = useState({ businessName: '', email: '', password: '', confirmPassword: '', plan: 'basic' });
  const [cycle,    setCycle]    = useState('monthly');
  const [currency, setCurrency] = useState('ngn');
  const [showPw,   setShowPw]   = useState(false);
  const [showCpw,  setShowCpw]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [pwStr,    setPwStr]    = useState({ score: 0, text: '', color: '' });
  const [psReady,  setPsReady]  = useState(false);

  useEffect(() => {
    if (!document.getElementById('fa-su')) {
      const fa = document.createElement('link'); fa.id = 'fa-su'; fa.rel = 'stylesheet';
      fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
      document.head.appendChild(fa);
    }
    if (!document.getElementById('ps-su')) {
      const s = document.createElement('script'); s.id = 'ps-su';
      s.src = 'https://js.paystack.co/v1/inline.js'; s.async = true;
      s.onload = () => setPsReady(true);
      document.body.appendChild(s);
    } else if (window.PaystackPop) {
      setPsReady(true);
    }
  }, []);

  const checkPw = (pw) => {
    let sc = 0;
    if (pw.length >= 8)  sc++;
    if (pw.length >= 12) sc++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) sc++;
    if (/\d/.test(pw))   sc++;
    if (/[^a-zA-Z\d]/.test(pw)) sc++;
    const m = [null,
      { text: 'Weak',        color: '#ef4444' },
      { text: 'Fair',        color: '#f59e0b' },
      { text: 'Good',        color: '#3b82f6' },
      { text: 'Strong',      color: '#10b981' },
      { text: 'Very Strong', color: '#059669' },
    ];
    setPwStr({ score: sc, ...(m[sc] || { text: '', color: '' }) });
  };

  const validate = () => {
    if (!form.businessName.trim()) { setError('Business name required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Valid email required'); return false; }
    if (form.password.length < 8)  { setError('Password must be 8+ characters'); return false; }
    if (!/[A-Z]/.test(form.password)) { setError('Password needs an uppercase letter'); return false; }
    if (!/\d/.test(form.password))    { setError('Password needs a number'); return false; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return false; }
    return true;
  };

  const createAccount = async (ref) => {
    try {
      const res = await axios.post(`${API_URL}/auth/signup`, {
        businessName: form.businessName,
        email:        form.email,
        password:     form.password,
        plan:         form.plan,
        billingCycle: cycle,
        currency,
        paystackReference: ref, // null for the free Basic plan
      });
      if (res.data.success) {
        AuthService.setUser(res.data.business, res.data.token);
        navigate('/onboarding');
      } else {
        setError(res.data.message || 'Failed to create account');
        setLoading(false);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (msg.toLowerCase().includes('already registered')) {
        sessionStorage.setItem('login_email',   form.email);
        sessionStorage.setItem('login_message', 'Email already registered. Please log in.');
        navigate('/login');
        return;
      }
      setError(msg || 'Failed to create account');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    const plan = PLANS.find(p => p.id === form.plan);

    // Basic is free — skip Paystack completely
    if (plan.id === 'basic') {
      setLoading(true);
      createAccount(null);
      return;
    }

    if (!psReady || !window.PaystackPop) {
      setError('Payment system still loading. Please wait 2 seconds and try again.');
      return;
    }

    setLoading(true);

    const amount = currency === 'usd'
      ? (cycle === 'yearly' ? plan.yearlyUSD : plan.monthlyUSD)
      : (cycle === 'yearly' ? plan.yearlyNGN : plan.monthlyNGN);

    const handler = window.PaystackPop.setup({
      key:      PS_KEY,
      email:    form.email,
      amount:   amount * 100,
      currency: currency === 'usd' ? 'USD' : 'NGN',
      ref:      `jmc_${form.plan}_${Date.now()}`,
      metadata: {
        custom_fields: [
          { display_name: 'Business Name', variable_name: 'business_name', value: form.businessName },
          { display_name: 'Plan',          variable_name: 'plan',          value: form.plan         },
          { display_name: 'Billing Cycle', variable_name: 'billing_cycle', value: cycle             },
          { display_name: 'Currency',      variable_name: 'currency',      value: currency           },
        ]
      },
      callback: (response) => {
        createAccount(response.reference);
      },
      onClose: () => {
        setError('Payment was cancelled. Please try again.');
        setLoading(false);
      },
    });
    handler.openIframe();
  };

  const sel = PLANS.find(p => p.id === form.plan);
  const selPrice = sel && (currency === 'usd'
    ? (cycle === 'yearly' ? `$${sel.yearlyUSD}/year` : `$${sel.monthlyUSD}/month`)
    : (cycle === 'yearly' ? `₦${sel.yearlyNGN.toLocaleString()}/year` : `₦${sel.monthlyNGN.toLocaleString()}/month`));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .su-pg{min-height:100vh;display:flex;font-family:'DM Sans',sans-serif;}
        .su-left{width:400px;flex-shrink:0;background:linear-gradient(160deg,#111827 0%,#1a0533 55%,#0f172a 100%);padding:40px 34px;display:flex;flex-direction:column;border-right:1px solid rgba(255,255,255,0.06);position:relative;overflow:hidden;}
        .su-left-orb{position:absolute;width:320px;height:320px;border-radius:50%;background:radial-gradient(circle,rgba(102,126,234,0.18),transparent 70%);bottom:-80px;right:-80px;pointer-events:none;}
        .su-right{flex:1;overflow-y:auto;padding:40px;background:#fafafa;display:flex;align-items:flex-start;justify-content:center;}
        .su-inner{width:100%;max-width:460px;}
        .su-logo{display:flex;align-items:center;gap:10px;margin-bottom:28px;}
        .su-cycle{display:flex;gap:5px;margin-bottom:12px;background:rgba(255,255,255,0.05);border-radius:10px;padding:4px;}
        .su-cyc{flex:1;padding:8px;border:none;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;background:transparent;color:rgba(255,255,255,0.45);font-family:'DM Sans',sans-serif;transition:all 0.2s;position:relative;}
        .su-cyc.on{background:#667eea;color:#fff;}
        .su-save{position:absolute;top:-8px;right:-4px;background:#10b981;color:#fff;font-size:8px;padding:1px 5px;border-radius:8px;font-weight:800;white-space:nowrap;}
        .su-plans{display:flex;flex-direction:column;gap:8px;}
        .su-plan{padding:12px 14px;border-radius:12px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);cursor:pointer;transition:all 0.2s;}
        .su-plan.on{background:rgba(102,126,234,0.12);border-color:rgba(102,126,234,0.4);}
        .su-plan-top{display:flex;align-items:center;gap:9px;margin-bottom:5px;}
        .su-plan-name{font-size:13px;font-weight:700;color:#fff;}
        .su-plan-price{margin-left:auto;font-size:14px;font-weight:800;}
        .su-perk{font-size:11px;color:rgba(255,255,255,0.4);display:flex;align-items:center;gap:5px;margin-bottom:2px;}
        .su-pop{font-size:9px;background:#667eea;color:#fff;padding:1px 6px;border-radius:20px;font-weight:800;}
        .su-title{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;color:#111827;margin-bottom:4px;}
        .su-sub{font-size:13px;color:#6b7280;margin-bottom:24px;}
        .su-alert{padding:11px 15px;border-radius:10px;font-size:13px;margin-bottom:16px;display:flex;align-items:flex-start;gap:8px;background:#fee2e2;border:1px solid #fca5a5;color:#b91c1c;}
        .su-grp{margin-bottom:15px;}
        .su-lbl{display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;}
        .su-wrap{position:relative;}
        .su-inp{width:100%;padding:11px 14px 11px 38px;border:2px solid #e5e7eb;border-radius:11px;font-size:14px;outline:none;font-family:'DM Sans',sans-serif;transition:border-color 0.2s;background:#fff;color:#111827;}
        .su-inp:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1);}
        .su-ico{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#9ca3af;font-size:13px;}
        .su-eye{position:absolute;right:11px;top:50%;transform:translateY(-50%);background:transparent;border:none;cursor:pointer;color:#9ca3af;font-size:14px;}
        .su-pw-bar{height:4px;border-radius:2px;margin-top:5px;transition:all 0.3s;}
        .su-hint{font-size:11px;color:#9ca3af;margin-top:3px;}
        .su-sel-sum{padding:11px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:11px;margin-bottom:14px;display:flex;align-items:center;gap:10px;}
        .su-pay-note{padding:9px 13px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:9px;margin-bottom:13px;font-size:12px;color:#1e40af;display:flex;align-items:center;gap:7px;}
        .su-btn{width:100%;padding:13px;border:none;border-radius:12px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;font-size:15px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 8px 20px rgba(102,126,234,0.3);transition:transform 0.2s;margin-top:6px;}
        .su-btn:hover:not(:disabled){transform:translateY(-1px);}
        .su-btn:disabled{opacity:0.65;cursor:not-allowed;}
        .su-login{text-align:center;font-size:13px;color:#6b7280;margin-top:18px;}
        .su-login a{color:#667eea;font-weight:700;text-decoration:none;}
        @media(max-width:860px){.su-pg{flex-direction:column;}.su-left{width:100%;border-right:none;border-bottom:1px solid rgba(255,255,255,0.06);}.su-plans{flex-direction:row;flex-wrap:wrap;}.su-plan{flex:1;min-width:140px;}.su-right{padding:30px 20px;}}
        @media(max-width:500px){.su-left{padding:26px 18px;}.su-right{padding:24px 16px;}.su-title{font-size:20px;}.su-plan{min-width:100%;}}
      `}</style>

      <div className="su-pg">
        {/* ── Left panel — plan selector ── */}
        <div className="su-left">
          <div className="su-left-orb" />
          <div className="su-logo">
            <img
              src={`${process.env.PUBLIC_URL}/janimony-logo.png`}
              alt="Janimony"
              style={{ width: '100px', height: 'auto', objectFit: 'contain' }}
            />
          </div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, marginBottom: 20 }}>
            Add AI-powered live chat to your website in under 60 seconds. Works on any platform.
          </p>

          {/* Currency toggle */}
          <div className="su-cycle">
            <button className={`su-cyc${currency === 'ngn' ? ' on' : ''}`} onClick={() => setCurrency('ngn')}>₦ NGN</button>
            <button className={`su-cyc${currency === 'usd' ? ' on' : ''}`} onClick={() => setCurrency('usd')}>$ USD</button>
          </div>

          {/* Billing cycle toggle */}
          <div className="su-cycle">
            <button className={`su-cyc${cycle === 'monthly' ? ' on' : ''}`} onClick={() => setCycle('monthly')}>Monthly</button>
            <button className={`su-cyc${cycle === 'yearly' ? ' on' : ''}`} onClick={() => setCycle('yearly')}>
              Yearly<span className="su-save">17% OFF</span>
            </button>
          </div>

          {/* Plan cards */}
          <div className="su-plans">
            {PLANS.map(p => {
              const price = p.id === 'basic'
                ? 'Free'
                : currency === 'usd'
                  ? (cycle === 'yearly' ? `$${p.yearlyUSD}` : `$${p.monthlyUSD}`)
                  : (cycle === 'yearly' ? `₦${p.yearlyNGN.toLocaleString()}` : `₦${p.monthlyNGN.toLocaleString()}`);
              return (
                <div
                  key={p.id}
                  className={`su-plan${form.plan === p.id ? ' on' : ''}`}
                  onClick={() => setForm({ ...form, plan: p.id })}
                >
                  <div className="su-plan-top">
                    <i className={`fa-solid ${p.icon}`} style={{ color: p.color, fontSize: 13 }}></i>
                    <span className="su-plan-name">{p.label}</span>
                    {p.popular && <span className="su-pop">POPULAR</span>}
                    <span className="su-plan-price" style={{ color: p.color }}>{price}</span>
                  </div>
                  {p.perks.map(pk => (
                    <div key={pk} className="su-perk">
                      <i className="fa-solid fa-check" style={{ color: p.color, fontSize: 9 }}></i>{pk}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fa-solid fa-circle-info" style={{ color: '#10b981', fontSize: 12 }}></i>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
              Basic is free forever. Pro and Premium are billed securely via Paystack.
            </span>
          </div>
        </div>

        {/* ── Right panel — form ── */}
        <div className="su-right">
          <div className="su-inner">
            <h1 className="su-title">Create your account</h1>
            <p className="su-sub">Choose a plan and get started in 60 seconds.</p>

            {error && (
              <div className="su-alert">
                <i className="fa-solid fa-triangle-exclamation"></i>{error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="su-grp">
                <label className="su-lbl">Business Name</label>
                <div className="su-wrap">
                  <i className="fa-solid fa-building su-ico"></i>
                  <input className="su-inp" type="text" placeholder="e.g., Acme Ltd."
                    value={form.businessName}
                    onChange={e => { setForm({ ...form, businessName: e.target.value }); setError(''); }}
                    required autoComplete="organization" />
                </div>
              </div>

              <div className="su-grp">
                <label className="su-lbl">Work Email</label>
                <div className="su-wrap">
                  <i className="fa-solid fa-envelope su-ico"></i>
                  <input className="su-inp" type="email" placeholder="you@company.com"
                    value={form.email}
                    onChange={e => { setForm({ ...form, email: e.target.value }); setError(''); }}
                    required autoComplete="email" />
                </div>
              </div>

              <div className="su-grp">
                <label className="su-lbl">Password</label>
                <div className="su-wrap">
                  <i className="fa-solid fa-lock su-ico"></i>
                  <input className="su-inp" type={showPw ? 'text' : 'password'} placeholder="8+ characters"
                    value={form.password} style={{ paddingRight: 38 }}
                    onChange={e => { setForm({ ...form, password: e.target.value }); setError(''); checkPw(e.target.value); }}
                    required autoComplete="new-password" />
                  <button type="button" className="su-eye" onClick={() => setShowPw(!showPw)}>
                    <i className={`fa-solid ${showPw ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {form.password && <div className="su-pw-bar" style={{ width: `${(pwStr.score / 5) * 100}%`, background: pwStr.color || '#e5e7eb' }}></div>}
                {pwStr.text && <p className="su-hint" style={{ color: pwStr.color }}>{pwStr.text}</p>}
              </div>

              <div className="su-grp">
                <label className="su-lbl">Confirm Password</label>
                <div className="su-wrap">
                  <i className="fa-solid fa-lock su-ico"></i>
                  <input className="su-inp" type={showCpw ? 'text' : 'password'} placeholder="Re-enter password"
                    value={form.confirmPassword} style={{ paddingRight: 38 }}
                    onChange={e => { setForm({ ...form, confirmPassword: e.target.value }); setError(''); }}
                    required autoComplete="new-password" />
                  <button type="button" className="su-eye" onClick={() => setShowCpw(!showCpw)}>
                    <i className={`fa-solid ${showCpw ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              {/* Selected plan summary */}
              {sel && (
                <div className="su-sel-sum">
                  <i className={`fa-solid ${sel.icon}`} style={{ color: sel.color, fontSize: 16 }}></i>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{sel.label} Plan</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {sel.id === 'basic' ? 'Free forever' : selPrice}
                    </div>
                  </div>
                  <i className="fa-solid fa-lock" style={{ color: '#10b981', fontSize: 12 }}></i>
                </div>
              )}

              {form.plan !== 'basic' && (
                <div className="su-pay-note">
                  <i className="fa-solid fa-shield-halved"></i>
                  Secure payment via Paystack — cards, bank transfer, USSD &amp; mobile money supported.
                </div>
              )}

              <button type="submit" className="su-btn" disabled={loading}>
                {loading
                  ? <><i className="fa-solid fa-spinner fa-spin"></i>Processing...</>
                  : form.plan === 'basic'
                    ? <><i className="fa-solid fa-user-plus"></i>Create Free Account</>
                    : <><i className="fa-solid fa-credit-card"></i>Pay &amp; Create Account</>}
              </button>
            </form>

            <p className="su-login">Already have an account? <Link to="/login">Sign in</Link></p>
            <p style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#9ca3af' }}>
              By signing up you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </>
  );
}